import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { isClickedOutside, onAnimationEnd } from '@theme/utilities';

const ANCHOR_FIELD_SELECTOR = '.ca-search-field-wrap .ca-search-field';

/**
 * Search dialog: anchored non-modal dropdown on desktop, modal sheet elsewhere; keep in lockstep with the `@supports (anchor-name: --x)` block in search-modal.liquid.
 * @extends DialogComponent
 */
class CaSearchDialog extends DialogComponent {
  /** Media query matching the desktop (dropdown) breakpoint. */
  #desktop = window.matchMedia('(min-width: 750px)');

  /** Evaluated once. */
  #supportsAnchor = typeof CSS !== 'undefined' && CSS.supports('anchor-name: --x');

  /** @returns {HTMLElement | null} The rendered header search field (the anchor), if any. */
  get #anchorField() {
    for (const el of document.querySelectorAll(ANCHOR_FIELD_SELECTOR)) {
      if (el instanceof HTMLElement && el.getClientRects().length > 0) return el;
    }
    return null;
  }

  /**
   * True when the panel opens as the anchored non-modal dropdown rather than the modal sheet.
   * @returns {boolean}
   */
  get #dropdownMode() {
    return this.#desktop.matches && this.#supportsAnchor && this.#anchorField !== null;
  }

  /** True while the current open used the non-modal desktop path. */
  #nonModalOpen = false;

  /**
   * Synchronous in-flight close guard: closeDialog is async and an outside click fires it twice.
   * @type {boolean}
   */
  #closing = false;

  /** Scroll position captured before a modal (mobile) open, restored on close. */
  #previousScrollY = 0;

  /**
   * Element focused before opening; restored on close because non-modal show() does not do it.
   * @type {HTMLElement | null}
   */
  #triggerEl = null;

  /** @type {ResizeObserver | null} */
  #anchorObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#desktop.addEventListener('change', this.#handleBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#desktop.removeEventListener('change', this.#handleBreakpointChange);
    this.#unobserveAnchor();
  }

  #unobserveAnchor() {
    this.#anchorObserver?.disconnect();
    this.#anchorObserver = null;
  }

  /** Closes the non-modal dropdown once its anchor field stops rendering (e.g. header switches to drawer). */
  #handleAnchorResize = () => {
    if (this.#nonModalOpen && this.refs.dialog?.open && this.#anchorField === null) {
      this.closeDialog();
    }
  };

  /** Closes the panel if the viewport crosses the 750px breakpoint while it is open. */
  #handleBreakpointChange = () => {
    if (this.refs.dialog?.open) {
      this.closeDialog();
    }
  };

  /**
   * Opens the search panel. Anchored desktop → non-modal dropdown; otherwise → modal sheet.
   * @override
   */
  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    if (this.#dropdownMode) {
      this.#openNonModal(dialog);
    } else {
      this.#openModal(dialog);
    }
  }

  /**
   * Closes the search panel, tearing down whichever open path is active.
   * @override
   */
  closeDialog = async () => {
    if (this.#closing) return;

    const { dialog } = this.refs;
    if (!dialog.open) return;

    this.#closing = true;

    this.#unobserveAnchor();
    document.removeEventListener('click', this.#handleDesktopClick, { capture: true });
    document.removeEventListener('keydown', this.#handleDesktopKeyDown);
    this.refs.dialog?.removeEventListener('focusout', this.#handleDesktopFocusOut);
    this.removeEventListener('click', this.#handleModalClick);
    this.removeEventListener('keydown', this.#handleModalKeyDown);

    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, { subtree: false });

    if (!this.#nonModalOpen) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    const wasNonModal = this.#nonModalOpen;

    dialog.close();
    dialog.classList.remove('dialog-closing');
    dialog.removeAttribute('data-ca-anchored');
    this.#nonModalOpen = false;

    for (const field of document.querySelectorAll(ANCHOR_FIELD_SELECTOR)) {
      field.setAttribute('aria-expanded', 'false');
    }
    if (wasNonModal) {
      const focusInDialog = !document.activeElement || dialog.contains(document.activeElement);
      if (focusInDialog && this.#triggerEl?.isConnected) {
        this.#triggerEl.focus();
      }
    }
    this.#triggerEl = null;

    this.#closing = false;

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Desktop: non-modal open. Page stays live (no body lock, no backdrop).
   * @param {HTMLDialogElement} dialog
   */
  #openNonModal(dialog) {
    this.#nonModalOpen = true;
    const active = document.activeElement;
    const anchor = this.#anchorField;
    this.#triggerEl = active instanceof HTMLElement ? active : anchor;
    anchor?.setAttribute('aria-expanded', 'true');
    dialog.setAttribute('data-ca-anchored', '');

    if (anchor && typeof ResizeObserver !== 'undefined') {
      this.#unobserveAnchor();
      this.#anchorObserver = new ResizeObserver(this.#handleAnchorResize);
      this.#anchorObserver.observe(anchor);
    }

    requestAnimationFrame(() => {
      dialog.show();
      this.dispatchEvent(new DialogOpenEvent());

      document.addEventListener('click', this.#handleDesktopClick, { capture: true });
      document.addEventListener('keydown', this.#handleDesktopKeyDown);

      const input = dialog.querySelector('.search-input');
      if (input instanceof HTMLElement) input.focus();

      // Attach focusout only after input.focus(), or the opening focus move closes the panel.
      dialog.addEventListener('focusout', this.#handleDesktopFocusOut);
    });
  }

  /**
   * Mobile: modal open with body scroll-lock (matches the shared dialog engine).
   * @param {HTMLDialogElement} dialog
   */
  #openModal(dialog) {
    this.#nonModalOpen = false;
    dialog.removeAttribute('data-ca-anchored');
    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleModalClick);
      this.addEventListener('keydown', this.#handleModalKeyDown);
    });
  }

  /**
   * Closes the modal (mobile) when the backdrop outside the dialog is clicked.
   * @param {MouseEvent} event
   */
  #handleModalClick = (event) => {
    if (isClickedOutside(event, this.refs.dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Closes the modal (mobile) on Escape, routing through closeDialog teardown.
   * @param {KeyboardEvent} event
   */
  #handleModalKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Closes the dropdown when clicking outside it (desktop non-modal only).
   * @param {MouseEvent} event
   */
  #handleDesktopClick = (event) => {
    const { dialog } = this.refs;
    const target = event.composedPath?.()[0] ?? event.target;

    if (target instanceof Element && target.closest('.ca-search-field-wrap')) return;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Closes the dropdown on Escape (desktop non-modal only).
   * @param {KeyboardEvent} event
   */
  #handleDesktopKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Desktop non-modal focus containment: close when focus leaves the panel, refocus the input when focus is lost.
   * @param {FocusEvent} event
   */
  #handleDesktopFocusOut = (event) => {
    if (!this.#nonModalOpen) return;
    const { dialog } = this.refs;
    if (!dialog?.open) return;

    const next = event.relatedTarget;

    if (next instanceof Node) {
      if (dialog.contains(next)) return;
      if (next instanceof Element && next.closest('.ca-search-field-wrap')) return;
      this.closeDialog();
      return;
    }

    requestAnimationFrame(() => {
      if (!dialog.open || !this.#nonModalOpen) return;
      const active = document.activeElement;
      const focusLost = !active || active === document.body;
      if (!focusLost) return;
      const input = dialog.querySelector('.search-input');
      if (input instanceof HTMLElement) input.focus();
    });
  };
}

if (!customElements.get('ca-search-dialog')) {
  customElements.define('ca-search-dialog', CaSearchDialog);
}
